import React, { useState, useEffect, useCallback, useRef } from 'react';
import CreditsBadge from '../components/CreditsBadge';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useBasket } from '../context/BasketContext';
import { USER_TYPES, currencySymbolOf } from '../constants/designer';
import { shopSocialHref, shopWebsiteHref } from '../constants/shop';
import { planOfShop } from '../constants/plans';
import { getGalleryItem, getGalleryFull } from '../firebase/galleryService';
import { fetchShop } from '../firebase/shopService';
import { ITEM_KINDS, blobIdsOf, hasPrice, productHref } from '../constants/gallery';
import { SOCIAL_KINDS } from '../firebase/socialService';
import { SocialBar } from '../components/Social';
import Comments from '../components/Comments';
import NavBrand from '../components/NavBrand';
import UserMenu from '../components/UserMenu';
import PublicNav from '../components/PublicNav';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import BackBar from '../components/BackBar';
import PageSpinner from '../components/PageSpinner';
import PersonIcon from '../components/PersonIcon';
import GuestGateModal from '../components/GuestGateModal';
import { useOpenChat } from '../hooks/useChat';
import useSeo from '../hooks/useSeo';
import { useChatUI } from '../context/ChatUIContext';
import './Dashboard.css';
import './ProductPage.css';

// ── ONE PRODUCT, AT ITS OWN ADDRESS ──────────────────────────────────────────
// Opening a listing used to mean going to the SHOP that sells it, with the
// viewer sprung open over it by `?photo=`. Closing the picture then left the
// reader on a shop page they never asked for, several steps from the catalogue
// they were browsing.
//
// A product now has an address of its own — which fixes the stranding and buys
// the thing a click-handler never could: a listing is a LINK. It opens in a new
// tab, bookmarks, and can be pasted to somebody.
//
// A PAGE, NOT THE VIEWER. The lightbox is a good way to glance at a picture and
// a poor way to consider a purchase: it is a black overlay with the price in a
// corner, and everything about it says "you are still where you were". This
// lays the listing out the way a shop does — the pictures on the left with a
// rail of the others beside them, and everything a buyer decides on stacked
// down the right: price, basket, and the two ways to reach the seller.
//
// WHAT IT DOES NOT INVENT. The price is the listing's own, the phone and the
// site are the SHOP's own (see constants/shop). There is no discount, no "good
// price" verdict and no stock count, because none of those exist in the data —
// a badge that is always on is not information.

// Full renditions live one per document. A post holds at most five (see
// GALLERY_LIMITS.imagesPerItem), fetched one at a time so the cover paints
// immediately and the rail fills in behind it.
const RAIL_MAX = 5;

export default function ProductPage() {
  const { id } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { has, toggle, isBusy } = useBasket();

  const [item, setItem] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [at, setAt] = useState(0);
  const [fullUrls, setFullUrls] = useState({});    // blobId -> data URL
  const [gate, setGate] = useState('');
  // The picture, handed the whole screen. See the note on the stage button.
  const [zoom, setZoom] = useState(false);

  // Opening a conversation is one document get — the id is derived from the two
  // uids — and then a hand-off to the messages overlay, whose own listener
  // lives there. Same pair the public profile uses, so "message this shop" is
  // one conversation however it was started.
  const { start: startChatWith, busy: chatBusy } = useOpenChat();
  const { openMessages } = useChatUI();

  // ARRIVED FROM INSIDE THE APP, or straight from a pasted link? React Router
  // stamps the first entry of a session with the key 'default', so this
  // separates "there is a page to go back to" from "this tab opened here".
  // Latched on mount, because navigating within the page must not change it.
  const cameFromApp = useRef(location.key !== 'default');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // ── THE LISTING, THEN WHO SELLS IT ────────────────────────────────────────
  // Two reads, the second only once the first has said whose it is. A hidden or
  // deleted listing comes back null rather than throwing — see getGalleryItem.
  useEffect(() => {
    setLoading(true);
    setItem(null);
    setShop(null);
    setAt(0);
    setFullUrls({});
    let live = true;
    (async () => {
      const row = await getGalleryItem(ITEM_KINDS.PHOTO, id).catch(() => null);
      if (!live) return;
      setItem(row);
      setLoading(false);
      if (!row?.ownerUid) return;
      // Cached for five minutes by the service, and the catalogue has usually
      // warmed it already, so this is normally free.
      const s = await fetchShop(row.ownerUid).catch(() => null);
      if (live) setShop(s);
    })();
    return () => { live = false; };
  }, [id]);

  // ── THE PICTURES ──────────────────────────────────────────────────────────
  // The metadata document carries only the COVER's thumbnail; the rest live one
  // blob document each. Fetched in order, one at a time: the cover is what the
  // page needs to paint, and a five-image post firing five parallel reads of
  // full-size data URLs is exactly the burst the metadata/blob split exists to
  // avoid.
  const blobIds = blobIdsOf(item).slice(0, RAIL_MAX);
  const blobKey = blobIds.join(',');
  useEffect(() => {
    if (!blobKey) return undefined;
    let live = true;
    (async () => {
      for (const bid of blobKey.split(',')) {
        if (!live) return;
        const url = await getGalleryFull(ITEM_KINDS.PHOTO, bid).catch(() => null);
        if (!live) return;
        if (url) setFullUrls(c => ({ ...c, [bid]: url }));
      }
    })();
    return () => { live = false; };
  }, [blobKey]);

  // ── ARRIVED FROM A SHARED CARD ────────────────────────────────────────────
  // A share opened in a chat lands here as ?from=<chatId>, and the navigation
  // closed the chat overlay on the way (it is state, not a route). So leaving
  // would drop the reader back on whatever page the conversation was open over,
  // with the conversation gone — on a phone, with no obvious way to it.
  //
  // Leaving REOPENS it. Same contract DesignerProfile has honoured for
  // `?photo=&from=` since shares existed; this page inherited the parameter
  // when share cards started pointing at it (see shareHref).
  const fromChat = new URLSearchParams(location.search).get('from') || '';
  const close = useCallback(() => {
    if (cameFromApp.current) navigate(-1);
    else navigate('/', { replace: true });
    if (fromChat) openMessages(fromChat);
  }, [navigate, fromChat, openMessages]);

  // ── THIS LISTING, BY NAME ─────────────────────────────────────────────────
  // ABOVE THE EARLY RETURNS, because a hook cannot run conditionally and there
  // are three of them below. Optional chaining throughout, since `item` is null
  // for the first render and on the not-found path; useSeo skips a call with no
  // title, so nothing is written until the row actually arrives and the tab
  // never flashes a half-built name.
  //
  // Same fallback chain as the visible `heading` below — a shop may publish a
  // photo and a price with no words at all, and "— Tunify" is not a title.
  const seoName = item?.title || item?.description
    || (item?.subtype ? t(`prod_sub_${item.subtype}`) : '')
    || (item?.type ? t(`shop_cat_${item.type}`) : '');
  useSeo({
    title: seoName ? `${seoName}${shop?.name ? ` — ${shop.name}` : ''} | Tunify` : '',
    description: seoName
      ? `${seoName}${shop?.name ? ` from ${shop.name}` : ''} on Tunify. See the details and place it in your own 3D room design.`
      : '',
    // The listing's own photograph, so a shared link previews the product
    // rather than the site's default living room.
    image: item?.thumbUrl || undefined,
  });

  if (loading) return <div className="pp-root"><PageSpinner /></div>;

  if (!item) {
    return (
      <div className="pp-root pp-root--gone">
        <p className="pp-gone">{t('prod_gone')}</p>
        <button type="button" className="pp-back" onClick={close}>{t('back')}</button>
      </div>
    );
  }

  // ── NOT A LISTING ─────────────────────────────────────────────────────────
  // `photos` holds a shop's stock AND a designer's work, and only the first has
  // a price or a link to buy. This page is built around those — the buy card,
  // Call, Message, the basket — so a designer's photograph rendered here would
  // be a product page with an empty box where the actions go and a byline that
  // never resolves, because the owner is not a shop.
  //
  // It belongs on its owner's profile, at the deep link that page already
  // understands. Sent on rather than refused: the reader asked to see a
  // specific thing and it exists, it just does not live here. Chiefly this is
  // share cards — one `SHARE_KINDS.PHOTO` covers both kinds and the card cannot
  // tell them apart, so the decision has to be made once the item is in hand.
  //
  // `replace`, so Back skips this page rather than bouncing off it again.
  const isListing = item.price != null || !!item.productUrl;
  if (!isListing && item.ownerUid) {
    const carry = fromChat ? `&from=${encodeURIComponent(fromChat)}` : '';
    return <Navigate to={`/designer/${item.ownerUid}?photo=${item.id}${carry}`} replace />;
  }

  const shopName = shop?.name || '';

  // WHAT THIS LISTING IS CALLED. A shop may publish a picture with a price and
  // no words at all — the grid renders an em-dash for that, which is fine on a
  // tile and dismal as a page heading. The subtype (then the category) is a
  // real name for the thing rather than a placeholder standing in for one, and
  // it is already translated. If even that is missing the heading is dropped
  // rather than printed empty.
  const heading = item.title || item.description
    || (item.subtype ? t(`prod_sub_${item.subtype}`) : '')
    || (item.type ? t(`shop_cat_${item.type}`) : '');
  // The cover's thumbnail stands in until its own bytes land, so the page never
  // paints an empty frame; a LATER image has no thumbnail of its own, so it
  // shows the cover meanwhile rather than nothing.
  const cover = fullUrls[blobIds[0]] || item.thumbUrl || '';
  const mainSrc = fullUrls[blobIds[at]] || (at === 0 ? item.thumbUrl : cover) || '';

  // ── THE TWO WAYS TO REACH THE SELLER ──────────────────────────────────────
  // The listing's own link first, the shop's storefront second: `productUrl` is
  // where THIS thing can be bought, which is a better answer than the shop's
  // home page whenever it exists.
  const callHref = shop?.socials?.phone ? shopSocialHref('phone', shop.socials.phone) : '';
  const siteHref = productHref(item.productUrl) || (shop?.website ? shopWebsiteHref(shop.website) : '');

  // Same rule as BasketButton: buyers get one, a shop does not keep a basket of
  // its own stock, and an unpriced photo is not stock at all.
  const sellable = hasPrice(item) && item.visible !== false;
  const canBasket = sellable && profile?.userType !== USER_TYPES.SHOP;
  const inBasket = has(item.id);
  const basketBusy = isBusy?.(item.id);

  const onBasket = () => {
    // A visitor without an account is asked for one over this page rather than
    // sent to /login and away from the thing they were about to save.
    if (!user?.uid) { setGate('basket'); return; }
    toggle(item);
  };

  // MESSAGE THE SHOP, about this product. A phone call suits some buyers and
  // not others, and the app already has conversations — so the third action is
  // the one that keeps the exchange inside it.
  //
  // Hidden on your own listing: openChat refuses a conversation with yourself,
  // and a button that cannot work is worse than no button.
  const canMessage = !!item.ownerUid && user?.uid !== item.ownerUid;
  const onMessage = async () => {
    if (!user?.uid) { setGate('message'); return; }
    const chatId = await startChatWith({
      uid: item.ownerUid,
      name: shopName,
      avatar: shop?.avatarUrl || '',
    });
    if (chatId) openMessages(chatId);
  };

  return (
    <div className="dash-root pp-page">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <PublicNav />}
        </div>
      </header>

      {/* `onBack` only when there is a conversation to return to — otherwise
          the bar keeps its own history-then-fallback rule, which handles a
          pasted link that has nothing behind it. */}
      <BackBar width={1240} onBack={fromChat ? close : undefined} />

      <main className="pp-main">
        {/* ── PICTURES ── */}
        <section className="pp-media">
          {/* The rail is absent on a single-image post rather than present and
              holding one tile: a column of one is not a chooser. */}
          {blobIds.length > 1 && (
            <div className="pp-rail" role="tablist" aria-label={item.title || ''}>
              {blobIds.map((bid, i) => (
                <button
                  key={bid}
                  type="button"
                  role="tab"
                  aria-selected={i === at}
                  className={`pp-thumb${i === at ? ' pp-thumb--on' : ''}`}
                  onClick={() => setAt(i)}
                >
                  {(fullUrls[bid] || (i === 0 ? item.thumbUrl : '')) ? (
                    <img src={fullUrls[bid] || item.thumbUrl} alt="" />
                  ) : (
                    // Its bytes have not arrived yet. A neutral block, not the
                    // cover — five identical tiles would say the post has five
                    // copies of the same picture.
                    <span className="pp-thumb-wait" />
                  )}
                </button>
              ))}
            </div>
          )}

          {/* THE PICTURE OPENS FULL SIZE. The frame here is letterboxed into a
              4:5 box beside two columns of text, which is the right size for
              deciding whether to look closer and the wrong one for looking
              closer — so pressing it hands the whole screen over to the
              photograph. A button, not a click handler on the image, so it is
              reachable by keyboard and announces itself. */}
          <button
            type="button"
            className="pp-stage"
            onClick={() => mainSrc && setZoom(true)}
            aria-label={t('prod_zoom')}
            title={t('prod_zoom')}
          >
            {mainSrc
              ? <img className="pp-main-img" src={mainSrc} alt={item.title || item.description || ''} />
              : <span className="pp-stage-empty" />}
          </button>
        </section>

        {/* ── WHAT IT IS ── */}
        <section className="pp-info">
          {heading && <h1 className="pp-title">{heading}</h1>}

          {/* Likes and views, and the count for the thread below. The bar is
              the same one the viewer and the feed use, so a like made here is
              the same like made anywhere. */}
          <div className="pp-social">
            <SocialBar kind={SOCIAL_KINDS.PHOTO} id={item.id} ownerUid={item.ownerUid} compact />
          </div>

          {/* The listing's own attributes — only the ones it actually carries.
              An empty row would be a heading promising a fact nobody entered.

              ABOVE THE DESCRIPTION. The specs are three fixed rows and the
              description is free text of any length, so this way the facts sit
              at a predictable place under the title on every listing rather
              than starting wherever the seller's paragraph happened to end. */}
          {(item.type || item.subtype || item.color) && (
            <dl className="pp-specs">
              {item.type && (
                <div className="pp-spec">
                  <dt>{t('prod_type')}</dt><dd>{t(`shop_cat_${item.type}`)}</dd>
                </div>
              )}
              {item.subtype && (
                <div className="pp-spec">
                  <dt>{t('prod_subtype')}</dt><dd>{t(`prod_sub_${item.subtype}`)}</dd>
                </div>
              )}
              {item.color && (
                <div className="pp-spec">
                  <dt>{t('prod_color')}</dt><dd>{t(`prod_color_${item.color}`)}</dd>
                </div>
              )}
            </dl>
          )}

          {/* The seller's own words, last in the column — the longest and least
              predictable block, so nothing below it moves with its length. */}
          {item.description && item.description !== heading && (
            <p className="pp-desc">{item.description}</p>
          )}
        </section>

        {/* ── WHAT A BUYER DOES ABOUT IT ──
            Sticky on a desktop, so the price and the actions stay put while
            the description and the thread scroll past them. */}
        <aside className="pp-buy">
          {/* ── WHO SELLS IT, AT THE TOP OF THE BUY CARD ───────────────────
              Moved here from under the title. Who you would be buying from is
              part of the decision to buy, not a caption on the picture — it
              belongs beside the price and the two ways to reach them, which is
              also the block that stays on screen while the rest scrolls.

              TIER WORN AS A BORDER, the same gold and silver the listing tiles
              carry in search, so a shop's standing reads the same wherever it
              appears. The shop is still loading — or is a designer rather than
              a shop — falls to free and gets the plain border: a lookup that
              has not landed must never dress an account in a tier it may not
              have. */}
          {item.ownerUid && (
            <Link
              to={`/shop/${item.ownerUid}`}
              className={`pp-shop pp-shop--${planOfShop(shop).id}`}
            >
              <span className="pp-shop-av">
                {shop?.avatarUrl ? <img src={shop.avatarUrl} alt="" /> : <PersonIcon />}
              </span>
              <span className="pp-shop-name">{shopName || '…'}</span>
              {/* A FACT ABOUT THE SHOP, not about this listing — it comes from
                  the shop's own profile, the same flag the search results and
                  the storefront show, so it cannot say one thing here and
                  another there. Rendered only when the flag is actually set:
                  the absence of a badge is not a claim that they do not
                  deliver, but a badge is a promise the seller made. */}
              {shop?.delivery && <span className="pp-ship">{t('shop_delivery')}</span>}
            </Link>
          )}

          {item.price != null && (
            <div className="pp-price">
              {item.price.toLocaleString()} <span className="pp-cur">{currencySymbolOf(item.currency)}</span>
            </div>
          )}

          {canBasket && (
            <button
              type="button"
              className={`pp-cta${inBasket ? ' pp-cta--on' : ''}`}
              onClick={onBasket}
              disabled={basketBusy}
              aria-pressed={inBasket}
            >
              {inBasket ? t('bsk_remove') : t('bsk_add')}
            </button>
          )}

          {/* CALL AND WEBSITE, in place of a checkout this app does not have.
              Buying happens with the seller — so the page offers the two ways
              to reach them and does not pretend to take the money.
              Each is rendered only if the shop actually gave it: a dead "Call"
              button is worse than no button. */}
          {callHref && (
            <a className="pp-alt" href={callHref}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
              </svg>
              {t('prod_call')}
            </a>
          )}

          {/* MESSAGE, under Call — the same choice the profile page offers,
              and the one a visitor without an account is asked to register
              for rather than being turned away at. */}
          {canMessage && (
            <button type="button" className="pp-alt" onClick={onMessage} disabled={chatBusy}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
              </svg>
              {chatBusy ? '…' : t('chat_message')}
            </button>
          )}

          {siteHref && (
            <a
              className="pp-alt"
              href={siteHref}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
                strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" />
              </svg>
              {t('shop_website')}
            </a>
          )}
        </aside>

        {/* ── THE THREAD ──
            Full width under everything: it is a conversation about the product,
            not a sidebar of it. Same collection and same component as the
            viewer's, so a comment left here is the comment seen there. */}
        <section className="pp-talk">
          <Comments col="photos" itemId={item.id} ownerUid={item.ownerUid} />
        </section>
      </main>

      {/* ── THE PICTURE, FULL SIZE ──
          Deliberately small: a black screen, the photograph at its own size,
          and the post's other pictures along the bottom if it has any. It is
          NOT GalleryLightbox — that carries the price, the likes, the comments
          and the share menu, every one of which is already on this page, so
          opening it here would be the page rendering itself over itself. */}
      {zoom && mainSrc && (
        <ProductZoom
          src={mainSrc}
          alt={heading}
          thumbs={blobIds.length > 1 ? blobIds : []}
          urls={fullUrls}
          coverUrl={item.thumbUrl}
          at={at}
          onPick={setAt}
          onClose={() => setZoom(false)}
        />
      )}

      {gate && (
        <GuestGateModal
          reason={gate}
          // WHOSE NAME depends on what they pressed. A basket holds the
          // PRODUCT; a message goes to the SHOP — "create an account to
          // message New" names the wrong party entirely.
          name={gate === 'message' ? shopName : heading}
          onClose={() => setGate('')}
        />
      )}
    </div>
  );
}

// ── FULL-SIZE VIEWER ─────────────────────────────────────────────────────────
// One picture, the whole screen, and a way back out. Portalled to <body> so no
// transformed or blurred ancestor on the page can trap a `position: fixed`
// child inside its own box.
//
// Arrow keys page through a multi-image post and Escape closes, because those
// are the two things every reader will try without being told. Picking from
// the strip changes the PAGE's selection too — coming back out of full size on
// a different picture than you went in with would be the viewer and the page
// disagreeing about what you are looking at.
function ProductZoom({ src, alt, thumbs, urls, coverUrl, at, onPick, onClose }) {
  const { t } = useT();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (!thumbs.length) return;
      if (e.key === 'ArrowRight') onPick((at + 1) % thumbs.length);
      if (e.key === 'ArrowLeft') onPick((at - 1 + thumbs.length) % thumbs.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [at, thumbs.length, onPick, onClose]);

  // The page behind holds still while this is open — otherwise a scroll gesture
  // over the photograph moves the catalogue underneath it.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  return createPortal(
    <div className="pz" onClick={onClose} role="dialog" aria-modal="true" aria-label={alt || ''}>
      <button
        type="button"
        className="pz-x"
        onClick={onClose}
        aria-label={t('close_tooltip')}
        title={t('close_tooltip')}
      >✕</button>

      {/* The image swallows its own click so pressing the photograph does not
          close the thing you opened to look at it. */}
      <img className="pz-img" src={src} alt={alt || ''} onClick={e => e.stopPropagation()} />

      {thumbs.length > 1 && (
        <div className="pz-strip" onClick={e => e.stopPropagation()}>
          {thumbs.map((bid, i) => (
            <button
              key={bid}
              type="button"
              className={`pz-thumb${i === at ? ' pz-thumb--on' : ''}`}
              onClick={() => onPick(i)}
              aria-label={String(i + 1)}
            >
              {(urls[bid] || (i === 0 ? coverUrl : ''))
                ? <img src={urls[bid] || coverUrl} alt="" />
                : <span className="pz-thumb-wait" />}
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}
